import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { alignmentAPI, imageLibraryAPI } from "../services/api";
import { PageWrapper, Card, Button, ShareAlignmentCard, FullScreenGlow } from "../components";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sun, Moon, Sparkles, Check, ChevronRight, 
  Flame, ArrowLeft, Calendar, Share2
} from "lucide-react";

// Time-based greeting
const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good morning", period: "morning" };
  if (hour < 17) return { text: "Good afternoon", period: "midday" };
  return { text: "Good evening", period: "evening" };
};

// Step icons
const StepIcon = ({ step, completed, active }) => {
  const icons = {
    morning: Sun,
    practice: Sparkles,
    evening: Moon
  };
  const Icon = icons[step] || Sparkles;
  
  return (
    <div className={`
      w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500
      ${completed 
        ? 'bg-[#4A6741] text-white shadow-lg shadow-[#4A6741]/20' 
        : active 
          ? 'bg-[#4A6741]/10 text-[#4A6741] ring-2 ring-[#4A6741]/30' 
          : 'bg-[#F0EFEB] text-[#9CA3AF]'
      }
    `}>
      {completed ? <Check size={20} /> : <Icon size={20} />}
    </div>
  );
};

// Step connector line
const StepConnector = ({ completed }) => (
  <div className={`
    flex-1 h-0.5 mx-3 transition-all duration-500
    ${completed ? 'bg-[#4A6741]' : 'bg-[#E6E2DD]'}
  `} />
);

export const DailyAlignmentPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [reflection, setReflection] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCompletionGlow, setShowCompletionGlow] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const { text: greeting, period } = getTimeGreeting();

  useEffect(() => {
    fetchData();
    fetchBackgroundImage();
  }, []);

  const fetchData = async () => {
    try {
      const res = await alignmentAPI.getToday();
      setData(res.data);
      // Set initial active step based on completion and time
      if (!res.data.completion.morning) {
        setActiveStep("morning");
      } else if (!res.data.completion.practice) {
        setActiveStep("practice");
      } else if (!res.data.completion.evening) {
        setActiveStep("evening");
      } else {
        setActiveStep(null); // All complete
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackgroundImage = async () => {
    try {
      const res = await imageLibraryAPI.getRandom("still_water");
      setBackgroundImage(res.data?.url);
    } catch (e) {
      // Fallback image
      setBackgroundImage("https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200");
    }
  };

  const handleComplete = async (step) => {
    setCompleting(true);
    try {
      const res = await alignmentAPI.complete(step, step === "evening" ? reflection : null);
      setData(prev => ({
        ...prev,
        entry: res.data.entry,
        streak: res.data.streak,
        completion: {
          ...prev.completion,
          [step]: true
        }
      }));
      
      // Trigger completion glow
      setShowCompletionGlow(true);
      setTimeout(() => setShowCompletionGlow(false), 1500);
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        // Move to next step
        if (step === "morning" && !data.completion.practice) {
          setActiveStep("practice");
        } else if (step === "practice" && !data.completion.evening) {
          setActiveStep("evening");
        } else {
          setActiveStep(null);
        }
      }, 2000);
      setReflection("");
    } catch (e) {
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  const steps = [
    { id: "morning", label: "Morning", sublabel: "Reflection" },
    { id: "practice", label: "Midday", sublabel: "Practice" },
    { id: "evening", label: "Evening", sublabel: "Integration" }
  ];

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
          <div className="space-y-4 text-center">
            <div className="w-12 h-12 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-[#6B7280] tracking-wide">Preparing your ritual...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const allComplete = data?.completion?.morning && data?.completion?.practice && data?.completion?.evening;
  const currentPrompt = activeStep && data?.prompts?.[activeStep];

  return (
    <PageWrapper>
      <div className="min-h-screen bg-[#FAFAF7]">
        {/* Hero Section with Background */}
        <div className="relative h-64 overflow-hidden">
          {backgroundImage && (
            <motion.div
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${backgroundImage})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#FAFAF7]" />
            </motion.div>
          )}
          
          {/* Back Button */}
          <button 
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            data-testid="daily-alignment-back-btn"
            className="absolute top-6 left-6 z-10 p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          {/* Header Content */}
          <div className="relative z-10 h-full flex flex-col justify-end px-6 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-white/80 text-sm uppercase tracking-[0.2em] mb-2">
                Daily Alignment
              </p>
              <h1 className="font-heading text-3xl text-white">
                {greeting}
              </h1>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-8 max-w-lg mx-auto -mt-4">
          {/* Streak & Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <Card className="p-5 bg-white/80 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#D4A373] to-[#C49565] rounded-full flex items-center justify-center">
                    <Flame className="text-white" size={22} />
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-[#1F2937]">{data?.streak || 0}</p>
                    <p className="text-sm text-[#6B7280]">day streak</p>
                  </div>
                </div>
                <button 
                  onClick={() => navigate("/alignment/history")}
                  className="flex items-center gap-2 text-sm text-[#4A6741] hover:underline"
                >
                  <Calendar size={16} />
                  History
                </button>
              </div>
            </Card>
          </motion.div>

          {/* Step Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center justify-center">
              {steps.map((step, idx) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => !data?.completion?.[step.id] && setActiveStep(step.id)}
                    disabled={data?.completion?.[step.id]}
                    className="flex flex-col items-center"
                  >
                    <StepIcon 
                      step={step.id} 
                      completed={data?.completion?.[step.id]} 
                      active={activeStep === step.id}
                    />
                    <p className={`text-xs mt-2 transition-colors ${
                      activeStep === step.id ? 'text-[#4A6741] font-medium' : 'text-[#6B7280]'
                    }`}>
                      {step.label}
                    </p>
                  </button>
                  {idx < steps.length - 1 && (
                    <StepConnector completed={data?.completion?.[steps[idx].id]} />
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Active Prompt Card */}
          <AnimatePresence mode="wait">
            {activeStep && currentPrompt && (
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="p-8 bg-white shadow-xl shadow-black/5" data-testid="alignment-prompt-card">
                  {/* Step Label */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className={`w-2 h-2 rounded-full ${
                      activeStep === 'morning' ? 'bg-amber-400' :
                      activeStep === 'practice' ? 'bg-emerald-400' : 'bg-indigo-400'
                    }`} />
                    <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                      {activeStep === 'morning' ? 'Morning Reflection' :
                       activeStep === 'practice' ? 'Midday Practice' : 'Evening Integration'}
                    </p>
                  </div>

                  {/* Prompt Text */}
                  <p className="font-heading text-2xl text-[#1F2937] leading-relaxed mb-8">
                    {currentPrompt.text}
                  </p>

                  {/* Category Tag */}
                  <div className="flex items-center justify-between mb-8">
                    <span className="inline-block px-3 py-1 bg-[#F0EFEB] text-[#6B7280] text-xs rounded-full capitalize">
                      {currentPrompt.category?.replace(/_/g, ' ')}
                    </span>
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#4A6741] hover:bg-[#4A6741]/5 rounded-full transition-colors"
                      data-testid="share-prompt-btn"
                    >
                      <Share2 size={16} />
                      Share
                    </button>
                  </div>

                  {/* Evening Reflection Input */}
                  {activeStep === 'evening' && (
                    <div className="mb-6">
                      <textarea
                        value={reflection}
                        onChange={(e) => setReflection(e.target.value)}
                        placeholder="Share a thought from today... (optional)"
                        className="w-full h-24 p-4 rounded-xl border border-[#E6E2DD] bg-[#FAFAF7] text-[#1F2937] placeholder-[#9CA3AF] resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6741]/20"
                        data-testid="evening-reflection-input"
                      />
                    </div>
                  )}

                  {/* Complete Button */}
                  <Button
                    onClick={() => handleComplete(activeStep)}
                    disabled={completing}
                    className="w-full h-14 text-base"
                    data-testid="complete-step-btn"
                  >
                    {completing ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Completing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {activeStep === 'morning' ? 'Set Intention' :
                         activeStep === 'practice' ? 'Complete Practice' : 'Close Day'}
                        <ChevronRight size={18} />
                      </span>
                    )}
                  </Button>
                </Card>
              </motion.div>
            )}

            {/* All Complete State */}
            {allComplete && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="p-10 bg-white text-center" data-testid="alignment-complete-card">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#4A6741] to-[#5A7D4F] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#4A6741]/20">
                    <Check className="text-white" size={36} />
                  </div>
                  <h2 className="font-heading text-2xl text-[#1F2937] mb-3">
                    Today is complete
                  </h2>
                  <p className="text-[#6B7280] leading-relaxed mb-8">
                    You've honored your practice today.<br />
                    Rest in this stillness.
                  </p>
                  <div className="flex gap-3 mb-4">
                    <Button variant="outline" onClick={() => navigate("/")} className="flex-1">
                      Return Home
                    </Button>
                    <Button onClick={() => navigate("/presence")} className="flex-1">
                      Daily Presence
                    </Button>
                  </div>
                  {/* Share completed day */}
                  <button
                    onClick={() => {
                      // Share the evening prompt (last of the day)
                      setActiveStep("evening");
                      setShowShareModal(true);
                    }}
                    className="flex items-center justify-center gap-2 w-full py-3 text-sm text-[#4A6741] hover:bg-[#4A6741]/5 rounded-xl transition-colors"
                    data-testid="share-complete-btn"
                  >
                    <Share2 size={16} />
                    Share your alignment
                  </button>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Animation Overlay with Completion Glow */}
          <AnimatePresence>
            {showSuccess && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-[#4A6741]/90 flex items-center justify-center z-50"
              >
                {/* Golden glow effect */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: [0, 0.4, 0], scale: [0.5, 1.5, 2] }}
                  transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div 
                    className="w-full h-full"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(184, 149, 108, 0.25) 0%, rgba(184, 149, 108, 0.08) 30%, transparent 60%)'
                    }}
                  />
                </motion.div>
                
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="text-center text-white relative z-10"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 completion-glow"
                  >
                    <Check size={40} className="check-glow" />
                  </motion.div>
                  <p className="text-xl font-heading">Step complete</p>
                  <p className="text-white/70 mt-2">Breathe. You are here.</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Gentle Reminder */}
          {!allComplete && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-center text-sm text-[#9CA3AF] mt-10"
            >
              Take your time. This is your practice.
            </motion.p>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareAlignmentCard
        prompt={currentPrompt || (activeStep && data?.prompts?.[activeStep])}
        stepType={activeStep || "evening"}
        isVisible={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </PageWrapper>
  );
};

export default DailyAlignmentPage;
